import { useCallback, useEffect, useRef, useState } from "react";

// HUB-454 (epic HUB-440, frontend-spec.md S5/S6): "one discount per order".
// This hook is the single source of truth for which discount is currently
// selected for THIS order — it does not invent a parallel store, it just
// extends B1/HUB-453's lifted state (hasStripeCoupon from campaignData,
// hasReviewEntitlement from ReviewOfferRow's eligibility poll) with one bit
// of client-only "which one did the user pick" state.
export type DiscountChoice = "review" | "coupon" | null;
export type DiscountSwapDirection = "reviewToCoupon" | "couponToReview";

export type UseDiscountExclusivityArgs = {
  /** campaignData.stripeCouponCode is set — a manual coupon is persisted on the order */
  hasStripeCoupon: boolean;
  /** an active (non-expired, non-redeemed) review discount entitlement exists for this client */
  hasReviewEntitlement: boolean;
  /**
   * Reuses the EXISTING coupon-clear path (OrderDetailsDrawer's
   * onUpdateCampaignCostWithCoupon(null, 0, 0), same call the "Remove" button
   * already makes) — no new coupon-clearing logic is introduced here.
   */
  onClearCoupon: () => Promise<void> | void;
  onSwapped?: (direction: DiscountSwapDirection) => void;
};

export const useDiscountExclusivity = ({
  hasStripeCoupon,
  hasReviewEntitlement,
  onClearCoupon,
  onSwapped,
}: UseDiscountExclusivityArgs) => {
  const [choice, setChoice] = useState<DiscountChoice>(null);
  const [swapDirection, setSwapDirection] = useState<DiscountSwapDirection | null>(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapError, setSwapError] = useState(false);
  // S1-blocked case (Greptile #1): the user has a coupon AND wants the review
  // discount, but has no entitlement yet — nothing to swap TO. We must not
  // clear the coupon until the entitlement actually lands, or the order
  // briefly has zero discounts. Instead we remember the intent and let the
  // effect below finish the swap automatically once hasReviewEntitlement
  // flips true.
  const [pendingReviewSwap, setPendingReviewSwap] = useState(false);

  const prevHasStripeCouponRef = useRef(hasStripeCoupon);

  // Derive the default choice, but keep an explicit swap decision "sticky"
  // until the discount backing it actually disappears server-side (entitlement
  // expired/redeemed, or there's nothing left to prefer at all). This is what
  // lets "review discount removed from the order UI but NOT consumed" (S5,
  // direction reviewToCoupon) survive re-renders even though hasReviewEntitlement
  // is still true — the entitlement stays intact, only deselected for this order.
  useEffect(() => {
    // Did the persisted coupon just get removed out from under an explicit
    // "coupon" choice (e.g. the existing "Remove" button, Greptile #2)? Track
    // the true->false transition specifically — a "coupon" choice with
    // hasStripeCoupon already false (mid reviewToCoupon swap / pending
    // couponToReview swap, before a new code has been entered/cleared) must
    // NOT be treated the same way, or it would bounce straight back.
    const couponWasRemoved = prevHasStripeCouponRef.current && !hasStripeCoupon;
    prevHasStripeCouponRef.current = hasStripeCoupon;

    setChoice((prev) => {
      if (prev === "review" && !hasReviewEntitlement) {
        return hasStripeCoupon ? "coupon" : null;
      }
      if (prev === "coupon" && couponWasRemoved) {
        return hasReviewEntitlement ? "review" : null;
      }
      if (prev === "coupon" && !hasStripeCoupon && !hasReviewEntitlement) {
        return null;
      }
      if (prev !== null) return prev;
      // No explicit choice yet: a pre-existing persisted coupon wins by
      // default (frontend-spec.md S5 — "code applied before review" renders
      // the review row in its blocked variant out of the box).
      if (hasStripeCoupon) return "coupon";
      if (hasReviewEntitlement) return "review";
      return null;
    });
  }, [hasStripeCoupon, hasReviewEntitlement]);

  // Finish a pending couponToReview swap the moment the entitlement lands —
  // this is the ONLY place the coupon gets cleared for that direction when it
  // started from S1 (no entitlement yet at confirm time).
  useEffect(() => {
    if (!pendingReviewSwap || !hasReviewEntitlement) return undefined;
    let cancelled = false;
    setIsSwapping(true);
    setSwapError(false);
    Promise.resolve(onClearCoupon())
      .then(() => {
        if (cancelled) return;
        setChoice("review");
        setPendingReviewSwap(false);
        onSwapped?.("couponToReview");
      })
      .catch((error) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("[useDiscountExclusivity] pending swap-to-review failed to clear the coupon", error);
        // Never leave the order with zero discounts: keep the coupon
        // selected (still applied server-side) and stop waiting — the row
        // falls back to its S4-blocked variant (entitlement is active now),
        // whose CTA goes through the manual confirm dialog's own retry path.
        setPendingReviewSwap(false);
      })
      .finally(() => {
        if (!cancelled) setIsSwapping(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pendingReviewSwap, hasReviewEntitlement, onClearCoupon, onSwapped]);

  const openSwapDialog = useCallback((direction: DiscountSwapDirection) => {
    setSwapError(false);
    setSwapDirection(direction);
  }, []);

  const startPendingReviewSwap = useCallback(() => {
    setPendingReviewSwap(true);
  }, []);

  const confirmSwap = useCallback(async () => {
    if (!swapDirection) return;

    if (swapDirection === "couponToReview" && !hasReviewEntitlement) {
      // Defensive (Greptile #1): confirming this direction before the
      // entitlement exists must never clear the coupon outright — fall back
      // to the pending flow instead of risking a zero-discount order.
      setSwapDirection(null);
      setSwapError(false);
      startPendingReviewSwap();
      return;
    }

    setIsSwapping(true);
    setSwapError(false);
    try {
      if (swapDirection === "couponToReview") {
        // Reuse the existing coupon-clear path. The entitlement is already
        // active at this point, so there is no window without a discount.
        await onClearCoupon();
        setChoice("review");
      } else {
        // Review discount is only DESELECTED for this order — never consumed.
        setChoice("coupon");
      }
      onSwapped?.(swapDirection);
      setSwapDirection(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[useDiscountExclusivity] swap failed", error);
      // Greptile #3: never flip `choice` (or close the dialog) on a failed
      // clear — the server still has the coupon, so the UI must keep it too.
      // The dialog stays open with an error + retry (re-calling confirmSwap)
      // or cancel (unchanged).
      setSwapError(true);
    } finally {
      setIsSwapping(false);
    }
  }, [swapDirection, hasReviewEntitlement, onClearCoupon, onSwapped, startPendingReviewSwap]);

  const cancelSwap = useCallback(() => {
    // Cancel => no change, per spec.
    setSwapDirection(null);
    setSwapError(false);
  }, []);

  return {
    discountChoice: choice,
    swapDirection,
    isSwapDialogOpen: swapDirection !== null,
    isSwapping,
    swapError,
    isPendingReviewSwap: pendingReviewSwap,
    /** true while either the dialog-driven swap or the auto pending swap is in flight — safe to disable CTAs on */
    isSwapBusy: isSwapping || pendingReviewSwap,
    openSwapDialog,
    startPendingReviewSwap,
    confirmSwap,
    cancelSwap,
  };
};
